import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { supabase } from '../../lib/supabase';

const MyTermDialog = ({ open, onClose }) => {
  const { subjects } = useApp();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    subject: '',
    term: '',
    definition: '',
    keywords: '',
    korean_definition: ''
  });

  // 과목 목록 정렬 및 필터링
  const subjectList = useMemo(() => {
    if (!subjects) return [];
    return Array.from(new Set(subjects))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'ko'));
  }, [subjects]);

  const resetForm = () => {
    setFormData({
      subject: '',
      term: '',
      definition: '',
      keywords: '',
      korean_definition: ''
    });
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSubmit = async () => {
    // 폼 검증
    if (!formData.subject || !formData.term || !formData.definition) {
      setError('과목, 용어, 정의는 필수 입력 항목입니다.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // 키워드를 배열로 변환
      const keywordsArray = formData.keywords
        ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
        : [];
      
      // 사용자 ID 가져오기
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('로그인이 필요합니다.');
      }
      
      // 데이터 저장
      const { data, error } = await supabase
        .from('my_terms')
        .insert([{
          user_id: userId,
          subject: formData.subject,
          term: formData.term,
          definition: formData.definition,
          keywords: keywordsArray,
          korean_definition: formData.korean_definition || null
        }])
        .select();
      
      if (error) {
        throw new Error(`저장 실패: ${error.message}`);
      }
      
      console.log('✅ 나만의 용어 추가 성공:', data);
      setSuccess(true);
      
      // 3초 후 다이얼로그 닫기
      setTimeout(() => {
        if (success) handleClose();
      }, 3000);
      
    } catch (err) {
      console.error('❌ 나만의 용어 추가 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 8
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          나만의 용어 추가
        </Typography>
        <IconButton 
          edge="end" 
          onClick={handleClose} 
          disabled={loading}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {success && (
            <Box sx={{ 
              bgcolor: 'success.light', 
              p: 2, 
              borderRadius: 1,
              mb: 2
            }}>
              <Typography color="success.dark" fontWeight="medium">
                나만의 용어가 성공적으로 추가되었습니다!
              </Typography>
            </Box>
          )}
          
          {error && (
            <Box sx={{ 
              bgcolor: 'error.light', 
              p: 2, 
              borderRadius: 1,
              mb: 2
            }}>
              <Typography color="error" fontWeight="medium">
                {error}
              </Typography>
            </Box>
          )}
        
          <FormControl fullWidth required>
            <InputLabel>과목</InputLabel>
            <Select
              value={formData.subject}
              onChange={handleChange('subject')}
              label="과목"
              disabled={loading}
            >
              {subjectList.map(subject => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="용어"
            value={formData.term}
            onChange={handleChange('term')}
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="정의"
            value={formData.definition}
            onChange={handleChange('definition')}
            required
            disabled={loading}
            helperText="용어에 대한 정의를 자세히 작성해주세요"
          />
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="한국어 설명 (선택사항)"
            value={formData.korean_definition}
            onChange={handleChange('korean_definition')}
            disabled={loading}
            helperText="용어에 대한 한국어 설명을 추가할 수 있습니다"
          />

          <TextField
            fullWidth
            label="키워드 (쉼표로 구분)"
            value={formData.keywords}
            onChange={handleChange('keywords')}
            disabled={loading}
            helperText="키워드는 쉼표(,)로 구분하여 입력하세요"
          />
          
          {formData.keywords && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {formData.keywords.split(',').map((keyword, index) => (
                keyword.trim() && (
                  <Chip 
                    key={index} 
                    label={keyword.trim()} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                )
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={loading}
        >
          취소
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.subject || !formData.term || !formData.definition}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyTermDialog;